import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Avatar, Typography, Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { TenantContext, UserProfile } from '../../types';
import { OrganizationMenu } from '../navigation/OrganizationMenu';
import { EventMenu } from '../navigation/EventMenu';
import { ThirdPartyMenu } from '../navigation/ThirdPartyMenu';
import { isModuleEnabled } from '../../utils/orgModules';
import { isLoyaltyFocus } from '../../utils/menuFocus';
import { EventSelector } from '../navigation/EventSelector';
import { useTranslation } from '../../hooks/useTranslation';

const { Sider } = Layout;
const { Text } = Typography;

interface TenantSiderProps {
  tenant: TenantContext;
  user: UserProfile;
  mode?: 'sider' | 'drawer';
  onNavigate?: () => void;
}

export const TenantSider: React.FC<TenantSiderProps> = ({ tenant, user, mode = 'sider', onNavigate }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // logoLoadFailed — fallback về Avatar/chữ cái đầu nếu logo_url bị hỏng (link chết, ảnh đã xoá khỏi
  // S3...) thay vì hiện icon ảnh vỡ trên nền tối của sidebar. Reset lại mỗi khi logo_url đổi (tổ chức
  // khác/vừa upload logo mới) để không giữ mãi trạng thái lỗi của link CŨ.
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  useEffect(() => {
    setLogoLoadFailed(false);
  }, [tenant.branding.logo_url]);
  const navigate = useNavigate();
  const location = useLocation();

  // Platform admin đang ở /admin thì dùng menu nền tảng; đang xem một tổ chức (/organizations/:slug) thì dùng menu tổ chức (ticket 113).
  const isPlatformUser = user.user_type === 'platform_user' && tenant.organization_slug === 'platform';
  const isPlatformViewingOrg = user.user_type === 'platform_user' && !isPlatformUser;

  // Menu state management
  const getSelectedKeysFromPath = () => {
    const currentPath = location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);

    // Platform Admin patterns (/admin/*)
    if (pathSegments[0] === 'admin') {
      if (pathSegments.includes('system')) return ['system-status'];
      if (pathSegments.includes('users')) return ['users'];
      if (pathSegments.includes('organizations')) return ['organizations'];
      if (pathSegments.includes('audit-logs')) return ['audit-logs'];
      // /admin (platform dashboard)
      return ['dashboard'];
    }

    // Check for CMS patterns
    if (pathSegments.includes('cms')) {
      // Check for settings routes first (more specific)
      if (pathSegments.includes('settings')) {
        if (pathSegments.includes('pages')) {
          return ['cms-settings-pages'];
        }
        if (pathSegments.includes('menus')) {
          return ['cms-settings-menus'];
        }
        if (pathSegments.includes('organization')) {
          return ['cms-settings-organization'];
        }
        if (pathSegments.includes('theme')) {
          return ['cms-settings-theme'];
        }
      }
      // Then check for content routes
      if (pathSegments.includes('articles')) {
        return ['cms-articles'];
      }
      if (pathSegments.includes('pages')) {
        return ['cms-pages'];
      }
      if (pathSegments.includes('tags')) {
        return ['cms-tags'];
      }
      if (pathSegments.includes('media')) {
        return ['cms-media'];
      }
      return ['cms'];
    }

    // Check for F&B/POS patterns
    if (pathSegments.includes('fnbpos')) {
      if (pathSegments.includes('outlets')) return ['fnbpos-outlets'];
      if (pathSegments.includes('orders')) return ['fnbpos-orders'];
      if (pathSegments.includes('staff')) return ['fnbpos-staff'];
      if (pathSegments.includes('masterdata')) return ['fnbpos-masterdata'];
      if (pathSegments.includes('warehouses')) return ['fnbpos-warehouses'];
      if (pathSegments.includes('menu')) return ['fnbpos-menu'];
      if (pathSegments.includes('materials')) return ['fnbpos-materials'];
      if (pathSegments.includes('stock')) return ['fnbpos-stock'];
      if (pathSegments.includes('purchasing')) return ['fnbpos-purchasing'];
      if (pathSegments.includes('reports')) return ['fnbpos-reports'];
      if (pathSegments.includes('terminals')) return ['fnbpos-terminals'];
      if (pathSegments.includes('reservations')) return ['fnbpos-reservations'];
      if (pathSegments.includes('kitchen-config')) return ['fnbpos-kitchen-config'];
      if (pathSegments.includes('floor')) return ['fnbpos-floor'];
      if (pathSegments.includes('selforder')) return ['fnbpos-selforder'];
      if (pathSegments.includes('takeaway')) return ['fnbpos-takeaway'];
      if (pathSegments.includes('booking-requests')) return ['fnbpos-booking-requests'];
      if (pathSegments.includes('tenant-config')) return ['fnbpos-tenant-config'];
      if (pathSegments.includes('bill-designer')) return ['fnbpos-bill-designer'];
      if (pathSegments.includes('report-designer')) return ['fnbpos-report-designer'];
      if (pathSegments.includes('payment-config')) return ['fnbpos-payment-config'];
      if (pathSegments.includes('payment-reconciliation')) return ['fnbpos-payment-reconciliation'];
      return ['fnbpos'];
    }

    // Check for event context patterns
    if (pathSegments.includes('events') && pathSegments.length >= 4) {
      // Check for specific routes first
      if (pathSegments.includes('checkinlists')) {
        return ['checkinlists'];
      }
      if (pathSegments.includes('checkin-devices')) {
        return ['checkin-devices'];
      }
      if (pathSegments.includes('items')) {
        return ['items'];
      }
      if (pathSegments.includes('categories')) {
        return ['categories'];
      }
      if (pathSegments.includes('questions')) {
        return ['questions'];
      }
      if (pathSegments.includes('quotas')) {
        return ['quotas'];
      }
      if (pathSegments.includes('subevents')) {
        return ['subevents'];
      }
      if (pathSegments.includes('groups')) {
        return ['groups'];
      }
      if (pathSegments.includes('orders')) {
        return ['orders'];
      }
      if (pathSegments.includes('vouchers')) {
        return ['vouchers'];
      }
      if (pathSegments.includes('gift-cards')) {
        return ['gift-cards'];
      }
      if (pathSegments.includes('thirdparty')) {
        if (pathSegments[pathSegments.indexOf('thirdparty') + 1] === 'products') {
          return ['thirdparty-products'];
        }
        if (pathSegments[pathSegments.indexOf('thirdparty') + 1] === 'inventory') {
          return ['thirdparty-inventory'];
        }
        return ['thirdparty-products'];
      }
      if (pathSegments.includes('settings')) {
        return ['event-settings'];
      }

      // Default to event dashboard
      return ['event-dashboard'];
    }

    // Check for Court module patterns — chỉ có 1 menu item con hiện tại ('court-venues'), nhưng route
    // tree còn có venues/:supplierId/courts, .../pricing, .../bookings (không có menu riêng) — tất cả
    // đều highlight về 'court-venues' vì cùng thuộc luồng quản lý sân từ trang venues (mirror pattern
    // fnbpos/cms ở trên — bug thật đã fix: nhánh 'court' từng bị thiếu hoàn toàn, rơi về 'org-dashboard').
    if (pathSegments.includes('court')) {
      return ['court-venues'];
    }
    if (pathSegments.includes('tournaments')) {
      return ['tournaments'];
    }

    // Check for thirdparty patterns
    if (pathSegments.includes('thirdparty')) {
      if (pathSegments.includes('suppliers')) {
        return ['suppliers'];
      }
      if (pathSegments.includes('products')) {
        return ['products'];
      }
      if (pathSegments.includes('inventory')) {
        // Check if it's bulk-import submenu
        if (pathSegments.includes('bulk-import')) {
          return ['inventory-bulk-import'];
        }
        // Default to inventory list view
        return ['inventory-list'];
      }
      if (pathSegments.includes('fulfillments')) {
        return ['fulfillments'];
      }
      if (pathSegments.includes('settlements')) {
        return ['settlements'];
      }
      if (pathSegments.includes('api-call-logs')) {
        return ['api-call-logs'];
      }
      return ['thirdparty'];
    }

    // Check for org-level patterns
    if (pathSegments.includes('organizations') && pathSegments.length >= 3) {
      const lastSegment = pathSegments[pathSegments.length - 1];

      // Check for nested routes under specific paths (e.g., giftcards/create, giftcards/{id}) — code
      // review: cùng lỗi đã sửa riêng cho tags/giftcards (G9-19) cũng xảy ra với customers/payments/
      // seating-plans/order-groups/thirdparty — orgPathMap bên dưới chỉ so khớp SEGMENT CUỐI, nên vào
      // trang con (customers/create, payments/:id, ...) lastSegment là "create"/ID, không khớp gì trong
      // map, rơi về mặc định "org-dashboard" — menu bên trái sáng sai (hoặc không sáng gì).
      if (pathSegments.includes('giftcards')) {
        return ['giftcards'];
      }
      // G9-19 — /tags/create, /tags/{id} đều phải highlight đúng menu "Tags" (không chỉ /tags trần).
      if (pathSegments.includes('tags')) {
        return ['tags'];
      }
      if (pathSegments.includes('loyalty-dashboard')) {
        return ['loyalty-dashboard'];
      }
      if (pathSegments.includes('crm-dashboard')) {
        return ['crm-dashboard'];
      }
      if (pathSegments.includes('customers')) {
        return ['customers'];
      }
      // Ticket 121: /users trước đây rơi về 'org-dashboard' (Tổng quan bị chọn nhầm).
      if (pathSegments.includes('users')) {
        return ['users'];
      }
      // Bug thật (2026-08-14, báo cáo trực tiếp): /organizations/:slug/vouchers không sáng menu "Phiếu
      // giảm giá" — thiếu check ở đây (cùng lỗi đã sửa cho giftcards/tags phía trên), rơi về mặc định
      // "org-dashboard" vì "vouchers" không có trong orgPathMap bên dưới.
      if (pathSegments.includes('vouchers')) {
        return ['vouchers'];
      }
      // Cùng lớp bug với vouchers ở trên (comment 2026-08-14) — promotions cũng rơi về mặc định
      // "org-dashboard" nếu thiếu check ở đây, dù route/click điều hướng đúng trang.
      if (pathSegments.includes('promotions')) {
        return ['promotions'];
      }
      if (pathSegments.includes('payments')) {
        return ['payments'];
      }
      if (pathSegments.includes('seating-plans')) {
        return ['seating-plans'];
      }
      if (pathSegments.includes('order-groups')) {
        return ['order-groups'];
      }
      // G9-32f/k6 — màn con /unit-ledger/{pair-config|settlement-runs|manual-entry|orphan-entries}
      // (settlement-runs còn có /:id chi tiết, vẫn phải sáng đúng menu cha).
      if (pathSegments.includes('unit-ledger')) {
        if (pathSegments.includes('pair-config')) return ['unit-ledger-pair-config'];
        if (pathSegments.includes('manual-entry')) return ['unit-ledger-manual-entry'];
        if (pathSegments.includes('orphan-entries')) return ['unit-ledger-orphan-entries'];
        if (pathSegments.includes('owned-events')) return ['unit-ledger-owned-events'];
        if (pathSegments.includes('internal-balances')) return ['unit-ledger-internal-balances'];
        if (pathSegments.includes('settlement-runs')) return ['unit-ledger-settlement-runs'];
        return ['unit-ledger'];
      }
      // 'thirdparty' đã được block riêng ở trên (dòng ~159) xử lý đầy đủ (suppliers/products/inventory/
      // fulfillments), luôn chặn trước khi chạy tới đây — không cần lặp lại ở block org-level này.

      // Map org paths to keys — chỉ còn dùng cho các route KHÔNG có trang con (flat), so khớp segment cuối.
      const orgPathMap: Record<string, string> = {
        [tenant.organization_slug]: 'org-dashboard',
        'events': 'events',
        'analytics': 'analytics',
        'settings': 'settings'
      };

      const orgKey = orgPathMap[lastSegment!] || 'org-dashboard';
      return [orgKey];
    }

    return ['org-dashboard'];
  };

  const selectedKeys = useMemo(() => {
    return getSelectedKeysFromPath();
  }, [location.pathname, tenant.organization_slug]);

  useEffect(() => {
    // Determine parent menu keys based on selected keys
    const parentKeys: string[] = [];

    selectedKeys.forEach((key: string) => {
      // Nhóm cấp 1 của menu tổ chức (ticket 121) — độc lập với chuỗi else-if bên dưới: 'vouchers'/'orders'... cũng
      // nằm trong nhánh 'event' nên nếu xếp chung sẽ không bao giờ mở nhóm 'Khuyến mãi'.
      if (['events', 'payments'].includes(key)) parentKeys.push('events-group');
      if (['promotions', 'vouchers', 'giftcards'].includes(key)) parentKeys.push('promo-group');
      if (['tags', 'seating-plans', 'order-groups'].includes(key)) parentKeys.push('other-group');
      // Platform Admin submenu items → keep the "platform-admin" parent open
      if (['system-status', 'users', 'organizations', 'audit-logs'].includes(key)) {
        parentKeys.push('platform-admin');
      }
      // CMS submenu items
      else if (['cms-articles', 'cms-pages', 'cms-tags', 'cms-media', 'cms-settings-pages', 'cms-settings-menus', 'cms-settings-organization', 'cms-settings-theme'].includes(key)) {
        parentKeys.push('cms');
      }
      // G9-32f/k6 — unit-ledger submenu items
      else if (['unit-ledger-pair-config', 'unit-ledger-settlement-runs', 'unit-ledger-manual-entry', 'unit-ledger-orphan-entries', 'unit-ledger-internal-balances', 'unit-ledger-owned-events'].includes(key)) {
        parentKeys.push('unit-ledger');
        parentKeys.push('other-group');
      }
      // F&B/POS submenu items
      else if (['fnbpos-outlets', 'fnbpos-orders', 'fnbpos-staff', 'fnbpos-masterdata', 'fnbpos-warehouses', 'fnbpos-menu', 'fnbpos-materials', 'fnbpos-stock', 'fnbpos-purchasing', 'fnbpos-reports', 'fnbpos-terminals', 'fnbpos-reservations', 'fnbpos-kitchen-config', 'fnbpos-floor', 'fnbpos-selforder', 'fnbpos-takeaway', 'fnbpos-booking-requests', 'fnbpos-tenant-config', 'fnbpos-bill-designer', 'fnbpos-report-designer', 'fnbpos-payment-config', 'fnbpos-payment-reconciliation'].includes(key)) {
        parentKeys.push('fnbpos');
      }
      // Event submenu items
      else if (['event-dashboard', 'checkinlists', 'checkin-devices', 'items', 'categories', 'questions', 'quotas', 'subevents', 'groups', 'orders', 'vouchers', 'gift-cards', 'event-settings', 'event'].includes(key)) {
        parentKeys.push('event');
      }
      // ThirdParty submenu items
      else if (['suppliers', 'products', 'inventory', 'fulfillments', 'settlements', 'api-call-logs'].includes(key)) {
        parentKeys.push('thirdparty');
      }
      // Inventory child items - need to open both inventory AND thirdparty parent
      else if (['inventory-list', 'inventory-bulk-import'].includes(key)) {
        parentKeys.push('inventory');
        parentKeys.push('thirdparty');
      }
      // Court submenu items — bug thật đã sửa: thiếu nhánh này khiến menu con 'court-venues' active
      // đúng nhưng menu cha 'court' không tự mở ra (cùng lỗi với getSelectedKeysFromPath từng thiếu
      // nhánh 'court' ở trên, đã fix riêng).
      else if (key === 'court-venues') {
        parentKeys.push('court');
      }
    });

    setOpenKeys(parentKeys);
  }, [selectedKeys]);


  const handleMenuClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  // ⚠ OrganizationMenu / EventMenu / ThirdPartyMenu thực chất là HOOK (useEventContext, useTranslation, useQuery...): PHẢI gọi vô điều
  // kiện ở mỗi lần render. Trước đây gọi trong nhánh `else` của menu platform nên khi platform admin chuyển /admin ↔ /organizations/:slug
  // (isPlatformUser đổi) số hook đổi → "Rendered more hooks than during the previous render" (ticket 113).
  const orgMenu = OrganizationMenu({
    tenant,
    user,
    selectedKeys,
    openKeys,
    onOpenChange: setOpenKeys,
    onMenuClick: handleMenuClick,
  });

  const eventMenu = EventMenu({
    tenant,
    user,
    selectedKeys,
    openKeys,
    onOpenChange: setOpenKeys,
    onMenuClick: handleMenuClick,
  });

  const thirdpartyMenu = ThirdPartyMenu({
    user,
    selectedKeys,
    openKeys,
    onOpenChange: setOpenKeys,
    onMenuClick: handleMenuClick,
  });

  const siderContent = (
    <>
      {/* Branding — platform admin gets the full Mantik wordmark logo (no Avatar/"M" initial, no
          org name text below); tenant orgs keep their own logo_url/Avatar+name branding unchanged. */}
      <div
        style={{
          padding: '16px',
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {isPlatformUser ? (
          <img
            src="/platform-logo.svg"
            alt="Mantik Platform"
            style={{ maxHeight: collapsed ? 42 : 60, maxWidth: '100%' }}
          />
        ) : (
          <>
            {tenant.branding.logo_url && !collapsed && !logoLoadFailed ? (
              <img
                src={tenant.branding.logo_url}
                alt={tenant.organization.name}
                style={{ maxHeight: 32, maxWidth: '100%' }}
                onError={() => setLogoLoadFailed(true)}
              />
            ) : (
              <Avatar
                size={collapsed ? 32 : 48}
                style={{
                  backgroundColor: tenant.branding.secondary_color || '#1890ff',
                  fontSize: collapsed ? 14 : 20,
                }}
              >
                {tenant.organization.name.charAt(0).toUpperCase()}
              </Avatar>
            )}
            {!collapsed && (
              <div style={{ marginTop: 8 }}>
                <Text strong style={{ color: '#fff', fontSize: 14 }}>
                  {tenant.organization.name}
                </Text>
                <br />
                <Text style={{ color: '#rgba(255,255,255,0.65)', fontSize: 12 }}>
                  {tenant.organization.subscription_tier}
                </Text>
              </div>
            )}
          </>
        )}
      </div>

      {/* Event Selector - only show for org users */}
      {!isPlatformUser && !isLoyaltyFocus() && <EventSelector />}

      {/* Navigation Menu */}
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        style={{
          borderRight: 0,
          background: 'transparent',
        }}
        theme="dark"
        inlineIndent={24}
        className="tenant-sidebar-menu"
        items={(() => {
          if (isPlatformUser) {
            // Platform Admin Menu - for platform users
            return [
              {
                key: 'dashboard',
                icon: <DashboardOutlined />,
                label: t('menu.platformAdmin.dashboard'),
                onClick: () => navigate('/admin'),
              },
              {
                key: 'platform-admin',
                icon: <SettingOutlined />,
                label: t('menu.platformAdmin.platformAdmin'),
                children: [
                  {
                    key: 'system-status',
                    label: t('menu.platformAdmin.systemStatus'),
                    onClick: () => navigate('/admin/system/status'),
                  },
                  {
                    key: 'users',
                    label: t('menu.platformAdmin.userManagement'),
                    onClick: () => navigate('/admin/users'),
                  },
                  {
                    key: 'organizations',
                    label: t('menu.platformAdmin.organizations'),
                    onClick: () => navigate('/admin/organizations'),
                  },
                  {
                    key: 'audit-logs',
                    label: t('menu.platformAdmin.auditLogs'),
                    onClick: () => navigate('/admin/audit-logs'),
                  },
                ],
              },
            ];
          } else {
            // Chế độ tập trung loyalty: không hiện menu sự kiện/bên thứ ba (chỉ menu cấp org đã lọc).
            if (isLoyaltyFocus()) return [...(orgMenu?.items || [])];
            const backToAdmin = isPlatformViewingOrg
              ? [{ key: 'back-to-platform-admin', icon: <SettingOutlined />, label: t('menu.platformAdmin.platformAdmin'), onClick: () => navigate('/admin') }]
              : [];
            return [
              ...backToAdmin,
              ...(orgMenu?.items || []),
              ...(eventMenu?.items || []),
              ...(isModuleEnabled(tenant.organization?.settings?.modules, 'events') ? thirdpartyMenu?.items || [] : []),
            ];
          }
        })()}
      />

      {/* Custom CSS for menu indentation */}
      <style>{`
        .tenant-sidebar-menu .ant-menu-submenu-title {
          padding-left: 16px !important;
        }

        .tenant-sidebar-menu .ant-menu-item {
          padding-left: 16px !important;
        }

        .tenant-sidebar-menu .ant-menu-submenu .ant-menu-item {
          padding-left: 48px !important;
          font-size: 14px;
        }

        .tenant-sidebar-menu .ant-menu-submenu .ant-menu-item .ant-menu-item-icon {
          margin-inline-end: 10px;
        }

        .tenant-sidebar-menu .ant-menu-item-only-child {
          padding-left: 16px !important;
        }
      `}</style>
    </>
  );

  if (mode === 'drawer') {
    return siderContent;
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={256}
      style={{
        background: '#0B2523',
        // Menu dài (F&B ~19 mục + các nhóm khác) phải cuộn riêng trong sider, nếu không mục cuối bị che (ticket 121).
        height: '100vh',
        position: 'sticky',
        top: 0,
        overflowY: 'auto',
      }}
      trigger={
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          style={{
            fontSize: '16px',
            width: '100%',
            height: 64,
            color: '#fff',
          }}
        />
      }
    >
      {siderContent}
    </Sider>
  );
};